import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import OtpInput from "@/components/OtpInput";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  PenNib, FilePdf, CheckCircle, Warning,
  ArrowSquareOut, DownloadSimple, Phone as PhoneIcon, User as UserIcon,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

/**
 * Iter113 + Iter115 — Pagina pubblica di firma documento con OTP SMS.
 * URL: /sign/{practice_id}/{file_id}?token=xxx&exp=yyy&rid=zzz
 *
 * Flusso:
 *  1. Vede il PDF (anteprima + download).
 *  2. Clicca "Firma con OTP" → modal con Nome, Cognome, Telefono.
 *  3. Riceve SMS con codice 6 cifre.
 *  4. Inserisce il codice → verifica → il backend genera automaticamente
 *     un PDF firmato con ricevuta OTP e chiude la pratica.
 */
export default function SignDocument() {
  const { practice_id, file_id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const exp = params.get("exp");
  const rid = params.get("rid");

  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [otpOpen, setOtpOpen] = useState(false);
  const [done, setDone] = useState(false);

  const qs = useMemo(() => {
    const s = new URLSearchParams();
    if (token) s.set("token", token);
    if (exp) s.set("exp", exp);
    if (rid) s.set("rid", rid);
    return s.toString();
  }, [token, exp, rid]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(
          `${API_BASE}/sign/${practice_id}/${file_id}?${qs}`,
        );
        setMeta(r.data);
        if (r.data.status === "signed") setDone(true);
      } catch (e) {
        const msg =
          e.response?.status === 403 ? "Il link non è valido o è scaduto." :
          e.response?.status === 404 ? "Richiesta di firma non trovata." :
          e.response?.status === 410 ? "Richiesta annullata dal mittente." :
          e.response?.data?.detail || "Errore nel caricare la pagina.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [practice_id, file_id, qs]);

  const onSigned = () => {
    setDone(true);
    setOtpOpen(false);
    toast.success("Documento firmato con successo");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-600 text-sm" data-testid="sign-loading">Caricamento…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center border border-red-100" data-testid="sign-error">
          <Warning size={40} weight="fill" className="mx-auto text-red-500 mb-3" />
          <h1 className="font-display text-xl font-bold text-slate-800 mb-2">Impossibile aprire</h1>
          <p className="text-sm text-slate-600">{error}</p>
          <p className="text-xs text-slate-400 mt-4">
            Se hai bisogno di aiuto scrivi a{" "}
            <a href="mailto:supporto@housetag.it" className="text-indigo-600 hover:underline">
              supporto@housetag.it
            </a>
          </p>
        </div>
      </div>
    );
  }

  const propLine = meta?.property
    ? [meta.property.address, meta.property.city].filter(Boolean).join(", ")
    : "";

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header brand */}
        <div className="mb-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center text-white">
            <PenNib size={20} weight="fill" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Housetag.it</div>
            <h1 className="font-display text-2xl font-bold text-slate-800">Firma documento</h1>
          </div>
        </div>

        {done ? (
          <div className="bg-white rounded-xl shadow-md p-8 border border-green-100" data-testid="sign-done">
            <div className="flex flex-col items-center text-center">
              <CheckCircle size={56} weight="fill" className="text-green-600 mb-3" />
              <h2 className="font-display text-xl font-bold text-slate-800 mb-2">
                Documento firmato correttamente
              </h2>
              <p className="text-sm text-slate-600 max-w-md">
                Grazie! Il documento è stato firmato tramite OTP SMS. Housetag ha
                generato una ricevuta con nome, cognome, numero verificato e
                timestamp che è stata allegata al PDF firmato.
              </p>
              <p className="text-xs text-slate-400 mt-4">Puoi chiudere questa pagina.</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
            {/* Meta della richiesta */}
            <div className="p-6 border-b bg-slate-50/60">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">
                Richiesta di firma
              </div>
              <h2 className="font-display text-xl font-bold text-slate-800 mb-3">
                {meta?.service_name || "Documento da firmare"}
              </h2>
              <div className="text-sm text-slate-700 space-y-1">
                {meta?.requested_by_name && (
                  <div><strong>Da:</strong> {meta.requested_by_name}</div>
                )}
                {meta?.professional_name && (
                  <div><strong>Professionista:</strong> {meta.professional_name}</div>
                )}
                {propLine && (
                  <div><strong>Immobile:</strong> {propLine}</div>
                )}
              </div>
              {meta?.message && (
                <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900 whitespace-pre-wrap" data-testid="sign-message">
                  <strong className="block mb-1">Messaggio:</strong>
                  {meta.message}
                </div>
              )}
            </div>

            {/* PDF originale */}
            <div className="p-6 border-b">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm">
                  <FilePdf size={22} weight="fill" className="text-red-600" />
                  <div>
                    <div className="font-medium text-slate-800" data-testid="sign-original-filename">
                      {meta?.original_filename || "documento.pdf"}
                    </div>
                    <div className="text-xs text-slate-500">Documento da firmare</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" asChild data-testid="sign-download-btn">
                    <a href={`${BACKEND_URL}${meta.signed_url}`} target="_blank" rel="noreferrer" download={meta?.original_filename}>
                      <DownloadSimple size={14} className="mr-1" /> Scarica
                    </a>
                  </Button>
                  <Button variant="outline" size="sm" asChild data-testid="sign-open-btn">
                    <a href={`${BACKEND_URL}${meta.signed_url}`} target="_blank" rel="noreferrer">
                      <ArrowSquareOut size={14} className="mr-1" /> Apri
                    </a>
                  </Button>
                </div>
              </div>

              <div className="mt-4 rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
                <iframe
                  title="Anteprima documento"
                  src={`${BACKEND_URL}${meta.signed_url}#toolbar=1&view=FitH`}
                  className="w-full h-[420px]"
                  data-testid="sign-pdf-iframe"
                />
              </div>
            </div>

            {/* CTA firma con OTP */}
            <div className="p-6 bg-indigo-50/60">
              <h3 className="font-display text-lg font-semibold text-slate-800 mb-2">
                Firma con OTP SMS
              </h3>
              <p className="text-sm text-slate-700 mb-4">
                Verifica la tua identità con un codice a 6 cifre che riceverai
                gratuitamente via SMS. Il documento verrà firmato digitalmente
                con una ricevuta che riporta nome, cognome, numero di telefono
                verificato, ora e IP.
              </p>
              <Button
                className="ht-gradient border-0 text-white"
                onClick={() => setOtpOpen(true)}
                data-testid="open-otp-modal-btn"
              >
                <PenNib size={16} weight="bold" className="mr-2" />
                Firma con OTP
              </Button>
              <p className="text-[11px] text-slate-500 mt-3">
                Riceverai un solo SMS con il codice. Se hai bisogno di aiuto
                scrivi a supporto@housetag.it.
              </p>
            </div>
          </div>
        )}

        <p className="text-center text-xs text-slate-400 mt-6">
          Housetag.it — assistenza:{" "}
          <a href="mailto:supporto@housetag.it" className="text-indigo-600 hover:underline">
            supporto@housetag.it
          </a>
        </p>
      </div>

      <OtpSignModal
        open={otpOpen}
        onClose={() => setOtpOpen(false)}
        practiceId={practice_id}
        fileId={file_id}
        qs={qs}
        onSigned={onSigned}
      />
    </div>
  );
}


function OtpSignModal({ open, onClose, practiceId, fileId, qs, onSigned }) {
  const [step, setStep] = useState(1); // 1 = anagrafica, 2 = codice
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [phoneMasked, setPhoneMasked] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setFirstName("");
      setLastName("");
      setPhone("");
      setCode("");
      setChallengeId("");
      setPhoneMasked("");
    }
  }, [open]);

  const submitStep1 = async (e) => {
    e?.preventDefault?.();
    const first = firstName.trim();
    const last = lastName.trim();
    const ph = phone.trim();
    if (!first || !last) {
      toast.error("Nome e cognome sono obbligatori");
      return;
    }
    if (!ph) {
      toast.error("Inserisci il numero di telefono");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(
        `${API_BASE}/sign/${practiceId}/${fileId}/otp/send?${qs}`,
        { first_name: first, last_name: last, phone: ph },
      );
      setChallengeId(r.data.challenge_id);
      setPhoneMasked(r.data.phone_masked || ph);
      setStep(2);
      toast.success(`Codice inviato a ${r.data.phone_masked || ph}`);
    } catch (e) {
      const msg =
        e.response?.status === 429 ? "Troppi tentativi. Riprova tra qualche minuto." :
        e.response?.status === 503 ? "Il servizio SMS non è configurato. Contatta l'assistenza." :
        e.response?.data?.detail || "Errore invio SMS. Riprova.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const submitStep2 = async (e) => {
    e?.preventDefault?.();
    // Iter195 — OpenAPI restituisce OTP a 5 cifre (default provider); accettiamo 5-6 cifre.
    if (!/^\d{5,6}$/.test(code)) {
      toast.error("Il codice deve essere di 5 o 6 cifre");
      return;
    }
    setVerifying(true);
    try {
      await axios.post(
        `${API_BASE}/sign/${practiceId}/${fileId}/otp/verify?${qs}`,
        { challenge_id: challengeId, code },
      );
      onSigned && onSigned();
    } catch (e) {
      const msg =
        e.response?.status === 410 ? "Codice scaduto. Richiedi un nuovo OTP." :
        e.response?.status === 429 ? "Troppi tentativi. Richiedi un nuovo OTP." :
        e.response?.status === 409 ? "Documento già firmato." :
        e.response?.data?.detail || "Codice non valido.";
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  const goBack = () => {
    setStep(1);
    setCode("");
    setChallengeId("");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!sending && !verifying && !v) onClose && onClose(); }}>
      <DialogContent className="max-w-md" data-testid="otp-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenNib size={18} weight="bold" />
            {step === 1 ? "Firma con OTP" : "Inserisci il codice OTP"}
          </DialogTitle>
          <DialogDescription>
            {step === 1 ? (
              <>Inserisci i tuoi dati per ricevere via SMS un codice a 6 cifre. Serve a certificare la firma del documento.</>
            ) : (
              <>Ti abbiamo inviato un SMS al numero <strong>{phoneMasked}</strong>.
                Inserisci il codice qui sotto per firmare. Il codice scade tra 5 minuti.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <form onSubmit={submitStep1} className="space-y-3 py-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Nome</label>
              <div className="relative">
                <UserIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-9"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value.slice(0, 60))}
                  maxLength={60}
                  placeholder="Mario"
                  data-testid="otp-first-name"
                  autoFocus
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Cognome</label>
              <div className="relative">
                <UserIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-9"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value.slice(0, 60))}
                  maxLength={60}
                  placeholder="Rossi"
                  data-testid="otp-last-name"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">
                Numero di telefono (Italia)
              </label>
              <div className="relative">
                <PhoneIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-9"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+39 333 123 4567"
                  inputMode="tel"
                  data-testid="otp-phone"
                />
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                Solo numeri di cellulare italiani. Formato accettato: 3331234567 o +393331234567.
              </div>
            </div>
            <DialogFooter className="gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={sending}
                data-testid="otp-cancel-1"
              >
                Annulla
              </Button>
              <Button
                type="submit"
                className="ht-gradient border-0 text-white"
                disabled={sending}
                data-testid="otp-send-btn"
              >
                {sending ? "Invio SMS…" : "Invia codice via SMS"}
              </Button>
            </DialogFooter>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submitStep2} className="space-y-3 py-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">
                Codice OTP ricevuto via SMS
              </label>
              {/* Iter207.22 — Sostituito input singolo con 6 mini-input
                  paste-friendly. Il gate `disabled` continua ad accettare 5 o 6
                  cifre per compatibilità con OpenAPI SMS (Iter194). */}
              <OtpInput
                value={code}
                onChange={setCode}
                length={6}
                autoFocus
                testIdPrefix="otp"
              />
              <div className="text-[10px] text-slate-500 mt-1 text-center">
                Non hai ricevuto l'SMS?{" "}
                <button
                  type="button"
                  className="text-indigo-600 hover:underline"
                  onClick={goBack}
                  data-testid="otp-back-btn"
                >
                  Cambia numero
                </button>
              </div>
            </div>
            <DialogFooter className="gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={verifying}
                data-testid="otp-cancel-2"
              >
                Annulla
              </Button>
              <Button
                type="submit"
                className="ht-gradient border-0 text-white"
                disabled={verifying || !/^\d{5,6}$/.test(code)}
                data-testid="otp-verify-btn"
              >
                {verifying ? "Verifica…" : "Firma documento"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
